import {
  extractLiveCiFulfillmentStatus,
  tableScoreTotal,
} from "./dpatLiveScoreExtractors.js";

export const MANUAL_SCORE_FIELDS = {
  TECHNICAL_TEAM: "technicalTeamScore",
  QUALITY_ASSURANCE: "qualityAssuranceScore",
  PETITION_COMMITTEE: "petitionCommitteeScore",
};

export const CI_SCORE_OPTIONS = [
  { value: "Fulfilled", label: "Fulfilled" },
  { value: "Not Fulfilled", label: "Not Fulfilled" },
];

export function slugFromTableCommentedId(tableCommentedId) {
  const text = String(tableCommentedId || "").trim();
  const match = text.match(/^(.+?)-(\d{4})$/);
  return match ? match[1] : text;
}

export function indicatorKindFromSlug(slug) {
  const normalized = String(slug || "").toLowerCase();
  if (/^c[\d.]/.test(normalized)) return "CI";
  if (/^sdi/.test(normalized)) return "SDI";
  if (/^pi/.test(normalized)) return "PI";
  return null;
}

export function compactCodeFromSlug(slug) {
  const text = String(slug || "");
  const dash = text.lastIndexOf("-");
  if (dash === -1) return text;
  return text.slice(dash + 1);
}

/** Maps DPAT indicator slug (without year) to assessment_start tables key. */
export const DPAT_SLUG_TO_TABLE_KEY = {
  "c1.0-1.1": "gaMeeting",
  "c1.0-1.2": "aapBudgetApproval",
  "c1.0-1.3": "subStructureMeeting",
  "c2.0-2.1": "executiveCommitteeMember",
  "c2.0-2.2": "subStructureCommitteeMeeting",
  "c2.0-2.3": "managementMeeting",
  "c2.0-2.4": "prccMeeting",
  "c2.0-2.5": "entityTenderCommitteeMeeting",
  "c3.0-3.1": "spcEntityTenderCommittee",
  "c3.0-3.2": "internalAuditUnitFunctionality",
  "c3.0-3.3": "auditCommitteeMeeting",
  "c4.0-4.1": "clientServiceFunctionality",
  "c4.0-4.2": "aapPublication",
  "c4.0-4.3": "auditorGeneralGAMeeting",
  "c4.0-4.4": "townHallMeeting",
  "sdi1.0-1.1": "generalAssemblyDecision",
  "sdi1.0-1.2": "generalAssemblyManagementActions",
  "sdi1.0-1.3": "gaSupport",
  "sdi2.0-2.1": "waterServices",
  "sdi2.0-2.2": "electricityServices",
  "sdi2.0-2.3": "sanitationServices",
  "sdi2.0-2.4": "maintenanceInfrastructure",
  "sdi2.0-2.5": "clientServiceCharter",
  "sdi2.0-2.6": "transportationNetworkService",
  "sdi3.0-3.1": "buildingInspectorateUnit",
  "sdi3.0-3.2": "permitProcessingIssuance",
  "sdi3.0-3.3": "streetNaming",
  "sdi4.0-4.1": "socialProtectionServices",
  "sdi4.0-4.2": "districtHotlineNumber",
  "sdi4.0-4.3": "pwdService",
  "sdi4.0-4.4": "deepeningGenderMainstreaming",
  "sdi4.0-4.5": "nutritionIntervention",
  "sdi5.0-5.1": "sanitationServiceProviders",
  "sdi5.0-5.2": "dumpingSite",
  "sdi5.0-5.3": "foodVendors",
  "sdi5.0-5.4": "publicSchoolFacility",
  "sdi5.0-5.5": "climateChangeIntervention",
  "sdi6.0-6.1": "districtLEDActivityPlan",
  "sdi6.0-6.2": "businessAndJobPromotion",
  "sdi6.0-6.3": "agroProcessingFacilitySupport",
  "sdi6.0-6.4": "businessCommunityEngagement",
  "pi1.0-1.1": "aapImplementation",
  "pi1.0-1.2": "monitoringProjectAndActivity",
  "pi1.0-1.3": "contractManagementAndAdmins",
  "pi1.0-1.4": "followUpDeduction",
  "pi1.0-1.5": "environmentalAndSocialSafeGuard",
  "pi2.0-2.1": "rateableRevenue",
  "pi3.0-3.1": "auditCommitteeResponsiveness",
  "pi3.0-3.2": "auditInfractions",
  "pi4.0-4.1": "educationServiceSupport",
  "pi4.0-4.2": "healthServiceSupport",
  "pi4.0-4.3": "agricultureSupport",
};

function parseNumericScore(value) {
  if (value == null || value === "") return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

export function readIndicatorManualScore(item, field) {
  const direct = item?.[field];
  const nested = item?.dddpData?.[field];
  const value = direct ?? nested ?? "";
  return String(value).trim();
}

export function hasEnteredManualScore(value) {
  const trimmed = String(value ?? "").trim();
  return Boolean(trimmed) && trimmed !== "—";
}

function parseCiFulfilled(value) {
  const trimmed = String(value ?? "").trim().toLowerCase();
  if (!trimmed || trimmed === "—") return null;
  if (["fulfilled", "yes", "true", "1"].includes(trimmed)) return true;
  if (["not fulfilled", "unfulfilled", "no", "false", "0"].includes(trimmed)) return false;
  return null;
}

function isSubtotalIndicatorRow(item) {
  return String(item?.no || "").toLowerCase().includes("sub-total");
}

function indicatorNo(item) {
  return String(item?.no ?? "").trim();
}

function isSubIndicatorNo(no) {
  const match = String(no || "").trim().match(/^(\d+)\.(\d+)$/);
  return Boolean(match && match[2] !== "0");
}

function isMajorIndicatorNo(no) {
  const text = String(no || "").trim();
  return /^\d+$/.test(text) || /^\d+\.0$/.test(text);
}

function majorIndicatorNo(no) {
  const text = String(no || "").trim();
  const match = text.match(/^(\d+)(?:\.\d+)?/);
  return match ? match[1] : null;
}

function emptyManualScores() {
  return {
    technicalTeamScore: "",
    qualityAssuranceScore: "",
    petitionCommitteeScore: "",
  };
}

/**
 * Petition > Quality Assurance > Technical Team > system score.
 * Blank role scores mean that role agrees with the current standing score.
 */
export function getSystemNumericScore(item) {
  // Prefer the preserved system fields only. Never treat final/current score as system.
  if (item?.systemActualScore != null && item.systemActualScore !== "") {
    const fromSystemActual = parseNumericScore(item.systemActualScore);
    if (fromSystemActual != null) return fromSystemActual;
  }
  if (item?.systemScore != null && item.systemScore !== "") {
    const fromSystemScore = parseNumericScore(item.systemScore);
    if (fromSystemScore != null) return fromSystemScore;
  }
  // First-pass API rows still store the DDDP score in actualScore before cascade.
  if (item?.systemActualScore == null && item?.systemScore == null) {
    return parseNumericScore(item?.actualScore) ?? 0;
  }
  return 0;
}

export function resolveRoleStandingNumericScores(item) {
  const systemScore = getSystemNumericScore(item);
  const technicalEntered = parseNumericScore(readIndicatorManualScore(item, MANUAL_SCORE_FIELDS.TECHNICAL_TEAM));
  const qaEntered = parseNumericScore(readIndicatorManualScore(item, MANUAL_SCORE_FIELDS.QUALITY_ASSURANCE));
  const petitionEntered = parseNumericScore(readIndicatorManualScore(item, MANUAL_SCORE_FIELDS.PETITION_COMMITTEE));

  const technicalStanding = technicalEntered != null ? technicalEntered : systemScore;
  const qualityAssuranceStanding = qaEntered != null ? qaEntered : technicalStanding;
  const petitionStanding = petitionEntered != null ? petitionEntered : qualityAssuranceStanding;
  const finalScore = petitionStanding;

  return {
    systemScore,
    technicalEntered,
    qaEntered,
    petitionEntered,
    technicalStanding,
    qualityAssuranceStanding,
    petitionStanding,
    finalScore,
  };
}

export function resolveFinalNumericScore(item) {
  return resolveRoleStandingNumericScores(item).finalScore;
}

export function getSystemCiFulfilled(item) {
  if (item?.systemFulfilled != null) return Boolean(item.systemFulfilled);
  // First-pass API rows still store the DDDP outcome in fulfilled before cascade.
  return Boolean(item?.fulfilled);
}

export function resolveRoleStandingCiStatuses(item) {
  const systemFulfilled = getSystemCiFulfilled(item);
  const technicalEntered = parseCiFulfilled(readIndicatorManualScore(item, MANUAL_SCORE_FIELDS.TECHNICAL_TEAM));
  const qaEntered = parseCiFulfilled(readIndicatorManualScore(item, MANUAL_SCORE_FIELDS.QUALITY_ASSURANCE));
  const petitionEntered = parseCiFulfilled(readIndicatorManualScore(item, MANUAL_SCORE_FIELDS.PETITION_COMMITTEE));

  const technicalStanding = technicalEntered != null ? technicalEntered : systemFulfilled;
  const qualityAssuranceStanding = qaEntered != null ? qaEntered : technicalStanding;
  const petitionStanding = petitionEntered != null ? petitionEntered : qualityAssuranceStanding;
  const finalFulfilled = petitionStanding;

  return {
    systemFulfilled,
    technicalEntered,
    qaEntered,
    petitionEntered,
    technicalStanding,
    qualityAssuranceStanding,
    petitionStanding,
    finalFulfilled,
  };
}

export function resolveFinalCiFulfilled(item) {
  return resolveRoleStandingCiStatuses(item).finalFulfilled;
}

function applyFinalScoresToCiList(items, childAggregates = null) {
  return (items || []).map((item) => {
    if (!item || isSubtotalIndicatorRow(item)) return item;
    if (item.childScoreAggregate) return item;

    const no = indicatorNo(item);
    // Parent CI (e.g. "1"): fulfilled only if ALL child sub-indicators are fulfilled.
    // A manual override on CI 1.1 must never mark all of CI 1 fulfilled.
    if (isMajorIndicatorNo(no)) {
      const childAggregate = childAggregates?.CI?.get(majorIndicatorNo(no));
      if (childAggregate) {
        return {
          ...item,
          ...childAggregate,
          technicalTeamScore: "",
          qualityAssuranceScore: "",
          petitionCommitteeScore: "",
          childScoreAggregate: true,
        };
      }

      const childRows = (items || []).filter(
        (row) =>
          row &&
          !isSubtotalIndicatorRow(row) &&
          isSubIndicatorNo(indicatorNo(row)) &&
          majorIndicatorNo(indicatorNo(row)) === majorIndicatorNo(no),
      );

      if (childRows.length > 0) {
        const childStandings = childRows.map((row) => resolveRoleStandingCiStatuses(row));
        return {
          ...item,
          systemFulfilled: childStandings.every((standing) => standing.systemFulfilled),
          fulfilled: childStandings.every((standing) => standing.finalFulfilled),
          technicalTeamScore: "",
          qualityAssuranceScore: "",
          petitionCommitteeScore: "",
          technicalTeamStandingFulfilled: childStandings.every((standing) => standing.technicalStanding),
          qualityAssuranceStandingFulfilled: childStandings.every(
            (standing) => standing.qualityAssuranceStanding,
          ),
          petitionCommitteeStandingFulfilled: childStandings.every((standing) => standing.petitionStanding),
        };
      }

      // No complete child snapshot: retain the existing fallback.
      const preservedTech = readIndicatorManualScore(item, MANUAL_SCORE_FIELDS.TECHNICAL_TEAM);
      const preservedQa = readIndicatorManualScore(item, MANUAL_SCORE_FIELDS.QUALITY_ASSURANCE);
      const preservedPetition = readIndicatorManualScore(item, MANUAL_SCORE_FIELDS.PETITION_COMMITTEE);
      const systemFulfilled = getSystemCiFulfilled({
        ...item,
        technicalTeamScore: "",
        qualityAssuranceScore: "",
        petitionCommitteeScore: "",
      });
      return {
        ...item,
        systemFulfilled,
        fulfilled: systemFulfilled,
        technicalTeamScore: preservedTech,
        qualityAssuranceScore: preservedQa,
        petitionCommitteeScore: preservedPetition,
        technicalTeamStandingFulfilled: systemFulfilled,
        qualityAssuranceStandingFulfilled: systemFulfilled,
        petitionCommitteeStandingFulfilled: systemFulfilled,
      };
    }

    const standing = resolveRoleStandingCiStatuses(item);
    return {
      ...item,
      systemFulfilled: standing.systemFulfilled,
      fulfilled: standing.finalFulfilled,
      technicalTeamScore: readIndicatorManualScore(item, MANUAL_SCORE_FIELDS.TECHNICAL_TEAM),
      qualityAssuranceScore: readIndicatorManualScore(item, MANUAL_SCORE_FIELDS.QUALITY_ASSURANCE),
      petitionCommitteeScore: readIndicatorManualScore(item, MANUAL_SCORE_FIELDS.PETITION_COMMITTEE),
      technicalTeamStandingFulfilled: standing.technicalStanding,
      qualityAssuranceStandingFulfilled: standing.qualityAssuranceStanding,
      petitionCommitteeStandingFulfilled: standing.petitionStanding,
    };
  });
}

function emptyRoleSums() {
  return {
    technicalTeamScore: 0,
    qualityAssuranceScore: 0,
    petitionCommitteeScore: 0,
  };
}

function applyFinalScoresToScoredList(items, kind, childAggregates = null, startScoreMap = null) {
  const roleStandingSums = emptyRoleSums();
  let systemSum = 0;
  let actualSum = 0;

  // Resolve each sub-indicator first. Parent/major rows are summed from children afterward
  // so a manual score on e.g. SDI 1.1 never replaces the SDI 1 total.
  const leafResolved = (items || []).map((item) => {
    if (!item || isSubtotalIndicatorRow(item)) return item;
    if (item.childScoreAggregate) return item;
    if (isMajorIndicatorNo(indicatorNo(item))) {
      return { ...item, __awaitMajor: true };
    }

    const overlayKey = scoreKeyFromSummaryItem(item, kind);
    const overlay = startScoreMap?.get(overlayKey);
    const standing = resolveRoleStandingNumericScores(
      overlay != null
        ? { ...item, systemActualScore: overlay, systemScore: overlay }
        : item,
    );
    return {
      ...item,
      systemActualScore: standing.systemScore,
      actualScore: standing.finalScore,
      technicalTeamScore: readIndicatorManualScore(item, MANUAL_SCORE_FIELDS.TECHNICAL_TEAM),
      qualityAssuranceScore: readIndicatorManualScore(item, MANUAL_SCORE_FIELDS.QUALITY_ASSURANCE),
      petitionCommitteeScore: readIndicatorManualScore(item, MANUAL_SCORE_FIELDS.PETITION_COMMITTEE),
      technicalTeamStandingScore: standing.technicalStanding,
      qualityAssuranceStandingScore: standing.qualityAssuranceStanding,
      petitionCommitteeStandingScore: standing.petitionStanding,
    };
  });

  const hasSubIndicatorRows = leafResolved.some(
    (item) => item && !isSubtotalIndicatorRow(item) && isSubIndicatorNo(indicatorNo(item)),
  );

  const next = leafResolved.map((item) => {
    if (!item || isSubtotalIndicatorRow(item)) return item;

    if (item.__awaitMajor) {
      const major = majorIndicatorNo(indicatorNo(item));
      const childRows = leafResolved.filter(
        (row) =>
          row &&
          !row.__awaitMajor &&
          !isSubtotalIndicatorRow(row) &&
          isSubIndicatorNo(indicatorNo(row)) &&
          majorIndicatorNo(indicatorNo(row)) === major,
      );

      const childAggregate = childAggregates?.[kind]?.get(major);
      if (childAggregate) {
        const { __awaitMajor, ...rest } = item;
        return {
          ...rest,
          ...childAggregate,
          technicalTeamScore: "",
          qualityAssuranceScore: "",
          petitionCommitteeScore: "",
          childScoreAggregate: true,
        };
      }

      if (hasSubIndicatorRows && childRows.length > 0) {
        const systemScore = childRows.reduce((sum, row) => sum + (Number(row.systemActualScore) || 0), 0);
        const finalScore = childRows.reduce((sum, row) => sum + (Number(row.actualScore) || 0), 0);
        const technicalStanding = childRows.reduce(
          (sum, row) => sum + (Number(row.technicalTeamStandingScore) || 0),
          0,
        );
        const qualityAssuranceStanding = childRows.reduce(
          (sum, row) => sum + (Number(row.qualityAssuranceStandingScore) || 0),
          0,
        );
        const petitionStanding = childRows.reduce(
          (sum, row) => sum + (Number(row.petitionCommitteeStandingScore) || 0),
          0,
        );
        const { __awaitMajor, ...rest } = item;
        return {
          ...rest,
          systemActualScore: systemScore,
          actualScore: finalScore,
          technicalTeamScore: "",
          qualityAssuranceScore: "",
          petitionCommitteeScore: "",
          technicalTeamStandingScore: technicalStanding,
          qualityAssuranceStandingScore: qualityAssuranceStanding,
          petitionCommitteeStandingScore: petitionStanding,
        };
      }

      // No complete child snapshot: retain the existing fallback.
      const preservedTech = readIndicatorManualScore(item, MANUAL_SCORE_FIELDS.TECHNICAL_TEAM);
      const preservedQa = readIndicatorManualScore(item, MANUAL_SCORE_FIELDS.QUALITY_ASSURANCE);
      const preservedPetition = readIndicatorManualScore(item, MANUAL_SCORE_FIELDS.PETITION_COMMITTEE);
      const systemScore = getSystemNumericScore({
        ...item,
        technicalTeamScore: "",
        qualityAssuranceScore: "",
        petitionCommitteeScore: "",
      });
      const { __awaitMajor, ...rest } = item;
      return {
        ...rest,
        systemActualScore: systemScore,
        actualScore: systemScore,
        technicalTeamScore: preservedTech,
        qualityAssuranceScore: preservedQa,
        petitionCommitteeScore: preservedPetition,
        technicalTeamStandingScore: systemScore,
        qualityAssuranceStandingScore: systemScore,
        petitionCommitteeStandingScore: systemScore,
      };
    }

    return item;
  });

  next.forEach((item) => {
    if (!item || isSubtotalIndicatorRow(item)) return;
    const no = indicatorNo(item);
    if (isSubIndicatorNo(no)) {
      const major = majorIndicatorNo(no);
      const parentAlreadyAggregated = next.some(
        (row) =>
          row?.childScoreAggregate &&
          isMajorIndicatorNo(indicatorNo(row)) &&
          majorIndicatorNo(indicatorNo(row)) === major,
      );
      if (parentAlreadyAggregated) return;
    }
    if (hasSubIndicatorRows && isMajorIndicatorNo(no) && !item.childScoreAggregate) return;

    systemSum += Number(item.systemActualScore) || 0;
    actualSum += Number(item.actualScore) || 0;
    roleStandingSums.technicalTeamScore += Number(item.technicalTeamStandingScore) || 0;
    roleStandingSums.qualityAssuranceScore += Number(item.qualityAssuranceStandingScore) || 0;
    roleStandingSums.petitionCommitteeScore += Number(item.petitionCommitteeStandingScore) || 0;
  });

  return {
    items: next.map((item) => {
      if (!item) return item;
      const { __awaitMajor, ...rest } = item;
      if (isSubtotalIndicatorRow(rest)) {
        return {
          ...rest,
          systemActualScore: systemSum,
          actualScore: actualSum,
          technicalTeamScore: roleStandingSums.technicalTeamScore,
          qualityAssuranceScore: roleStandingSums.qualityAssuranceScore,
          petitionCommitteeScore: roleStandingSums.petitionCommitteeScore,
          technicalTeamStandingScore: roleStandingSums.technicalTeamScore,
          qualityAssuranceStandingScore: roleStandingSums.qualityAssuranceScore,
          petitionCommitteeStandingScore: roleStandingSums.petitionCommitteeScore,
        };
      }
      return rest;
    }),
    systemSum,
    actualSum,
    roleSums: roleStandingSums,
  };
}

function scoreLookupKey(kind, code) {
  const text = String(code || "").trim();
  if (!kind || !text) return null;
  // Prefer sub-indicator codes (1.1) when present in the label.
  const subMatch = text.match(/(\d+\.\d+)/);
  if (subMatch) return `${kind}:${subMatch[1]}`;
  const majorMatch = text.match(/^(\d+)\b/);
  if (majorMatch) return `${kind}:${majorMatch[1]}`;
  return `${kind}:${text}`;
}

function scoreKeyFromSlug(slug) {
  const kind = indicatorKindFromSlug(slug);
  return scoreLookupKey(kind, compactCodeFromSlug(slug));
}

function scoreKeyFromSummaryItem(item, kind) {
  return scoreLookupKey(kind, item?.no);
}

function manualScoresFromCommentRecord(comment) {
  const fromDddp = parseManualScoresFromComments([comment]);
  // Some API payloads also store role scores on the comment root.
  return {
    technicalTeamScore:
      fromDddp.technicalTeamScore ||
      String(comment?.technicalTeamScore ?? "").trim() ||
      "",
    qualityAssuranceScore:
      fromDddp.qualityAssuranceScore ||
      String(comment?.qualityAssuranceScore ?? "").trim() ||
      "",
    petitionCommitteeScore:
      fromDddp.petitionCommitteeScore ||
      String(comment?.petitionCommitteeScore ?? "").trim() ||
      "",
  };
}

function scoreMapEntryHasValues(scores) {
  return Boolean(
    String(scores?.technicalTeamScore || "").trim() ||
      String(scores?.qualityAssuranceScore || "").trim() ||
      String(scores?.petitionCommitteeScore || "").trim(),
  );
}

export function buildManualScoreMapFromComments(comments) {
  const map = new Map();

  (comments || []).forEach((comment) => {
    const slug = slugFromTableCommentedId(comment?.tableCommented || comment?.dddpData?.indicator);
    const key = scoreKeyFromSlug(slug);
    if (!key) return;

    const parsed = manualScoresFromCommentRecord(comment);
    if (!scoreMapEntryHasValues(parsed) && !map.has(key)) {
      // Gap/text-only comments must not create empty map entries that wipe API manuals.
      return;
    }

    const existing = map.get(key) || emptyManualScores();
    map.set(key, {
      technicalTeamScore: parsed.technicalTeamScore || existing.technicalTeamScore || "",
      qualityAssuranceScore: parsed.qualityAssuranceScore || existing.qualityAssuranceScore || "",
      petitionCommitteeScore: parsed.petitionCommitteeScore || existing.petitionCommitteeScore || "",
    });
  });

  return map;
}

function mergeManualScoresIntoList(items, kind, scoreMap) {
  return (items || []).map((item) => {
    if (!item || isSubtotalIndicatorRow(item)) return item;
    // Parent/thematic rows must not inherit a child sub-indicator's manual score.
    if (isMajorIndicatorNo(indicatorNo(item))) {
      return {
        ...item,
        technicalTeamScore: "",
        qualityAssuranceScore: "",
        petitionCommitteeScore: "",
      };
    }
    const key = scoreKeyFromSummaryItem(item, kind);
    const scores = key ? scoreMap.get(key) : null;

    const pick = (field) => {
      const fromComments = scores?.[field];
      if (fromComments != null && String(fromComments).trim() !== "") {
        return String(fromComments).trim();
      }
      // Fall back to summary/API value so manuals still show when comment keying misses.
      return readIndicatorManualScore(item, field) || "";
    };

    return {
      ...item,
      technicalTeamScore: pick(MANUAL_SCORE_FIELDS.TECHNICAL_TEAM),
      qualityAssuranceScore: pick(MANUAL_SCORE_FIELDS.QUALITY_ASSURANCE),
      petitionCommitteeScore: pick(MANUAL_SCORE_FIELDS.PETITION_COMMITTEE),
    };
  });
}

export function mergeManualScoresIntoSummary(summary, comments) {
  if (!summary || typeof summary !== "object") return summary;
  const scoreMap = buildManualScoreMapFromComments(comments);
  if (scoreMap.size === 0) {
    // Still run apply path via caller; keep API manuals intact when no comment overrides exist.
    return summary;
  }

  return {
    ...summary,
    complianceIndicators: mergeManualScoresIntoList(summary.complianceIndicators, "CI", scoreMap),
    serviceDeliveryIndicators: mergeManualScoresIntoList(summary.serviceDeliveryIndicators, "SDI", scoreMap),
    performanceIndicators: mergeManualScoresIntoList(summary.performanceIndicators, "PI", scoreMap),
  };
}

function assessmentStartTablesFromComments(comments) {
  let best = null;
  (comments || []).forEach((comment) => {
    const dddp = normalizeCommentDddpData(comment?.dddpData);
    const tables = dddp?.tables && typeof dddp.tables === "object" ? dddp.tables : null;
    if (!tables) return;
    const isStart =
      String(comment?.tableCommented || dddp?.indicator || "").toLowerCase() ===
      "assessment_start_dapt";
    const keyCount = Object.keys(tables).length;
    if (isStart && keyCount > 0) {
      best = tables;
      return;
    }
    if (!best && keyCount > 5) best = tables;
  });
  return best;
}

function tablesFromIndicatorComments(comments) {
  const tables = {};
  (comments || []).forEach((comment) => {
    const slug = slugFromTableCommentedId(comment?.tableCommented || comment?.dddpData?.indicator);
    const tableKey = DPAT_SLUG_TO_TABLE_KEY[String(slug || "").toLowerCase()];
    if (!tableKey || tables[tableKey]) return;
    const dddp = normalizeCommentDddpData(comment?.dddpData);
    const rawTables = dddp?.tables;
    if (!rawTables || typeof rawTables !== "object") return;
    tables[tableKey] = rawTables[tableKey] || rawTables;
  });
  return tables;
}

function manualOverrideFlags(scores) {
  return {
    technicalTeamHasOverride: hasEnteredManualScore(scores?.technicalTeamScore),
    qualityAssuranceHasOverride: hasEnteredManualScore(scores?.qualityAssuranceScore),
    petitionCommitteeHasOverride: hasEnteredManualScore(scores?.petitionCommitteeScore),
  };
}

function childSystemNumericScore(entry) {
  if (!entry || typeof entry !== "object") return 0;
  const fields = ["score", "scorei", "scoreii", "scoreiii"];
  const hasScoreField = fields.some((field) => typeof entry[field] === "number");
  if (hasScoreField) return tableScoreTotal(entry);
  if (entry.data && typeof entry.data === "object") {
    const nestedHas = fields.some((field) => typeof entry.data[field] === "number");
    if (nestedHas) return tableScoreTotal(entry.data);
  }
  return (
    parseNumericScore(entry.gaDecisionScore) ??
    parseNumericScore(entry.data?.gaDecisionScore) ??
    0
  );
}

function resolveSummaryScoreTables(comments, districtContext = {}) {
  const startTables = assessmentStartTablesFromComments(comments) || {};
  const commentTables = tablesFromIndicatorComments(comments);
  return { ...commentTables, ...startTables };
}

function buildStartScoreMap(tables) {
  const map = new Map();
  if (!tables) return map;
  Object.entries(DPAT_SLUG_TO_TABLE_KEY).forEach(([slug, tableKey]) => {
    const kind = indicatorKindFromSlug(slug);
    if (kind !== "SDI" && kind !== "PI") return;
    if (!tables[tableKey]) return;
    map.set(`${kind}:${compactCodeFromSlug(slug)}`, childSystemNumericScore(tables[tableKey]));
  });
  return map;
}

function buildChildScoreAggregates(comments, tablesOverride = null) {
  const tables = tablesOverride || {
    ...(tablesFromIndicatorComments(comments) || {}),
    ...(assessmentStartTablesFromComments(comments) || {}),
  };
  if (Object.keys(tables).length === 0) return null;

  const scoreMap = buildManualScoreMapFromComments(comments);
  const groups = { CI: new Map(), SDI: new Map(), PI: new Map() };

  Object.entries(DPAT_SLUG_TO_TABLE_KEY).forEach(([slug, tableKey]) => {
    const kind = indicatorKindFromSlug(slug);
    const code = compactCodeFromSlug(slug);
    const major = majorIndicatorNo(code);
    if (!kind || !major) return;

    const entry = tables[tableKey];
    if (!entry) return;

    const scores = scoreMap.get(scoreKeyFromSlug(slug)) || emptyManualScores();
    const current = groups[kind].get(major) || { childCount: 0 };
    current.childCount += 1;

    if (kind === "CI") {
      const systemFulfilled =
        String(extractLiveCiFulfillmentStatus(tableKey, entry)).trim().toLowerCase() ===
        "fulfilled";
      const standing = resolveRoleStandingCiStatuses({
        systemFulfilled,
        ...scores,
      });
      current.systemFulfilled =
        current.systemFulfilled !== false && standing.systemFulfilled;
      current.fulfilled =
        current.fulfilled !== false && standing.finalFulfilled;
      current.technicalTeamStandingFulfilled =
        current.technicalTeamStandingFulfilled !== false && standing.technicalStanding;
      current.qualityAssuranceStandingFulfilled =
        current.qualityAssuranceStandingFulfilled !== false &&
        standing.qualityAssuranceStanding;
      current.petitionCommitteeStandingFulfilled =
        current.petitionCommitteeStandingFulfilled !== false && standing.petitionStanding;
    } else {
      const standing = resolveRoleStandingNumericScores({
        systemActualScore: childSystemNumericScore(entry),
        ...scores,
      });
      current.systemActualScore =
        (current.systemActualScore || 0) + standing.systemScore;
      current.actualScore = (current.actualScore || 0) + standing.finalScore;
      current.technicalTeamStandingScore =
        (current.technicalTeamStandingScore || 0) + standing.technicalStanding;
      current.qualityAssuranceStandingScore =
        (current.qualityAssuranceStandingScore || 0) +
        standing.qualityAssuranceStanding;
      current.petitionCommitteeStandingScore =
        (current.petitionCommitteeStandingScore || 0) + standing.petitionStanding;
    }

    const flags = manualOverrideFlags(scores);
    current.technicalTeamHasOverride =
      current.technicalTeamHasOverride || flags.technicalTeamHasOverride;
    current.qualityAssuranceHasOverride =
      current.qualityAssuranceHasOverride || flags.qualityAssuranceHasOverride;
    current.petitionCommitteeHasOverride =
      current.petitionCommitteeHasOverride || flags.petitionCommitteeHasOverride;
    groups[kind].set(major, current);
  });

  Object.keys(groups).forEach((kind) => {
    groups[kind].forEach((aggregate) => {
      delete aggregate.childCount;
    });
  });

  return groups;
}

function subCodeFromIndicatorNo(no) {
  const pairs = [...String(no || "").matchAll(/(\d+)\.(\d+)/g)];
  if (!pairs.length) return null;
  const last = pairs[pairs.length - 1];
  if (last[2] === "0") return null;
  return `${last[1]}.${last[2]}`;
}

function findSummarySubRow(items, code) {
  const wanted = String(code || "").trim();
  if (!wanted) return null;
  return (
    (items || []).find((item) => {
      if (!item || isSubtotalIndicatorRow(item) || item.childScoreAggregate) return false;
      if (isMajorIndicatorNo(indicatorNo(item))) return false;
      const no = indicatorNo(item);
      return subCodeFromIndicatorNo(no) === wanted || no === wanted;
    }) ?? null
  );
}

function pickMergedManualScores(scoreMap, slug, summaryRow) {
  const fromComments = scoreMap.get(scoreKeyFromSlug(slug)) || emptyManualScores();
  return {
    technicalTeamScore:
      fromComments.technicalTeamScore ||
      readIndicatorManualScore(summaryRow, MANUAL_SCORE_FIELDS.TECHNICAL_TEAM) ||
      "",
    qualityAssuranceScore:
      fromComments.qualityAssuranceScore ||
      readIndicatorManualScore(summaryRow, MANUAL_SCORE_FIELDS.QUALITY_ASSURANCE) ||
      "",
    petitionCommitteeScore:
      fromComments.petitionCommitteeScore ||
      readIndicatorManualScore(summaryRow, MANUAL_SCORE_FIELDS.PETITION_COMMITTEE) ||
      "",
  };
}

export function buildSubIndicatorDetailLists(summary, comments = [], tables = {}) {
  const scoreMap = buildManualScoreMapFromComments(comments);
  const lists = { CI: [], SDI: [], PI: [] };

  Object.entries(DPAT_SLUG_TO_TABLE_KEY).forEach(([slug, tableKey]) => {
    const kind = indicatorKindFromSlug(slug);
    const code = compactCodeFromSlug(slug);
    if (!kind || !code || !lists[kind]) return;

    const entry = tables?.[tableKey];
    const summaryList =
      kind === "CI"
        ? summary?.complianceIndicators
        : kind === "SDI"
          ? summary?.serviceDeliveryIndicators
          : summary?.performanceIndicators;
    const summaryRow = findSummarySubRow(summaryList, code);
    const manuals = pickMergedManualScores(scoreMap, slug, summaryRow);

    if (kind === "CI") {
      let systemFulfilled = null;
      if (entry) {
        systemFulfilled =
          String(extractLiveCiFulfillmentStatus(tableKey, entry)).trim().toLowerCase() === "fulfilled";
      } else if (summaryRow?.systemFulfilled != null) {
        systemFulfilled = Boolean(summaryRow.systemFulfilled);
      } else if (summaryRow) {
        systemFulfilled = getSystemCiFulfilled({
          ...summaryRow,
          technicalTeamScore: "",
          qualityAssuranceScore: "",
          petitionCommitteeScore: "",
        });
      }

      const hasData = systemFulfilled != null || scoreMapEntryHasValues(manuals);
      const item = {
        no: code,
        slug,
        thematicArea: summaryRow?.thematicArea || "",
        ...manuals,
        hasData,
        systemFulfilled,
        fulfilled: systemFulfilled,
      };

      if (hasData) {
        const standing = resolveRoleStandingCiStatuses({
          ...item,
          systemFulfilled: systemFulfilled ?? false,
        });
        item.systemFulfilled = standing.systemFulfilled;
        item.fulfilled = standing.finalFulfilled;
        item.technicalTeamStandingFulfilled = standing.technicalStanding;
        item.qualityAssuranceStandingFulfilled = standing.qualityAssuranceStanding;
        item.petitionCommitteeStandingFulfilled = standing.petitionStanding;
      }

      lists.CI.push(item);
      return;
    }

    let systemScore = null;
    if (entry) {
      systemScore = childSystemNumericScore(entry);
    } else if (summaryRow) {
      const fromSummary = getSystemNumericScore({
        ...summaryRow,
        technicalTeamScore: "",
        qualityAssuranceScore: "",
        petitionCommitteeScore: "",
      });
      if (fromSummary != null) systemScore = fromSummary;
    }

    const hasData = systemScore != null || scoreMapEntryHasValues(manuals);
    const item = {
      no: code,
      slug,
      thematicArea: summaryRow?.thematicArea || "",
      maxScore: extractMaxScoreFromTableEntry(entry) ?? summaryRow?.maxScore ?? null,
      ...manuals,
      hasData,
      systemActualScore: systemScore,
      systemScore,
      actualScore: systemScore,
    };

    if (hasData) {
      const standing = resolveRoleStandingNumericScores({
        ...item,
        systemActualScore: systemScore ?? 0,
        systemScore: systemScore ?? 0,
      });
      item.systemActualScore = standing.systemScore;
      item.systemScore = standing.systemScore;
      item.actualScore = standing.finalScore;
      item.technicalTeamStandingScore = standing.technicalStanding;
      item.qualityAssuranceStandingScore = standing.qualityAssuranceStanding;
      item.petitionCommitteeStandingScore = standing.petitionStanding;
    }

    lists[kind].push(item);
  });

  return lists;
}

export function applyFinalScoresToSummary(summary, comments = [], districtContext = {}) {
  if (!summary || typeof summary !== "object") return summary;

  const scoreTables = resolveSummaryScoreTables(comments, districtContext);
  const startScoreMap = buildStartScoreMap(scoreTables);
  const childAggregates = buildChildScoreAggregates(comments, scoreTables);
  const complianceIndicators = applyFinalScoresToCiList(
    summary.complianceIndicators,
    childAggregates,
  );

  const sdi = applyFinalScoresToScoredList(
    summary.serviceDeliveryIndicators,
    "SDI",
    childAggregates,
    startScoreMap,
  );
  const pi = applyFinalScoresToScoredList(
    summary.performanceIndicators,
    "PI",
    childAggregates,
    startScoreMap,
  );
  const totals = summary.totals || {};
  const subIndicators = buildSubIndicatorDetailLists(
    {
      complianceIndicators,
      serviceDeliveryIndicators: sdi.items,
      performanceIndicators: pi.items,
    },
    comments,
    scoreTables,
  );

  return {
    ...summary,
    complianceIndicators,
    serviceDeliveryIndicators: sdi.items,
    performanceIndicators: pi.items,
    ciSubIndicators: subIndicators.CI,
    sdiSubIndicators: subIndicators.SDI,
    piSubIndicators: subIndicators.PI,
    totals: {
      ...totals,
      sdiTotalSystemScore: sdi.systemSum,
      piTotalSystemScore: pi.systemSum,
      grandTotalSystemScore: sdi.systemSum + pi.systemSum,
      sdiTotalActualScore: sdi.actualSum,
      piTotalActualScore: pi.actualSum,
      grandTotalActualScore: sdi.actualSum + pi.actualSum,
      sdiTechnicalTeamTotalScore: sdi.roleSums.technicalTeamScore,
      sdiQualityAssuranceTotalScore: sdi.roleSums.qualityAssuranceScore,
      sdiPetitionCommitteeTotalScore: sdi.roleSums.petitionCommitteeScore,
      piTechnicalTeamTotalScore: pi.roleSums.technicalTeamScore,
      piQualityAssuranceTotalScore: pi.roleSums.qualityAssuranceScore,
      piPetitionCommitteeTotalScore: pi.roleSums.petitionCommitteeScore,
      technicalTeamTotalScore: sdi.roleSums.technicalTeamScore + pi.roleSums.technicalTeamScore,
      qualityAssuranceTotalScore: sdi.roleSums.qualityAssuranceScore + pi.roleSums.qualityAssuranceScore,
      petitionCommitteeTotalScore: sdi.roleSums.petitionCommitteeScore + pi.roleSums.petitionCommitteeScore,
    },
  };
}

export function resolveScoreSheetSummaryPayload(summaryPayload, comments = [], districtContext = {}) {
  let raw = summaryPayload;
  if (raw?.data && (raw.data.complianceIndicators || raw.data.serviceDeliveryIndicators || raw.data.performanceIndicators)) {
    raw = raw.data;
  }
  return applyFinalScoresToSummary(
    mergeManualScoresIntoSummary(raw, comments),
    comments,
    districtContext,
  );
}

export function extractMaxScoreFromTableEntry(entry) {
  if (!entry || typeof entry !== "object") return null;
  const direct = parseNumericScore(entry.maxScore);
  if (direct != null) return direct;
  const nested = parseNumericScore(entry.data?.maxScore);
  if (nested != null) return nested;
  return null;
}

export function getMaxScoreFromAssessmentTables(assessmentStartTables, slug) {
  if (!assessmentStartTables || !slug) return null;
  const tableKey = DPAT_SLUG_TO_TABLE_KEY[String(slug).toLowerCase()];
  if (!tableKey) return null;
  return extractMaxScoreFromTableEntry(assessmentStartTables[tableKey]);
}

export function findSummaryRowForSlug(summaryData, slug) {
  if (!summaryData || !slug) return null;
  const kind = indicatorKindFromSlug(slug);
  const code = compactCodeFromSlug(slug);
  const list =
    kind === "CI"
      ? summaryData.complianceIndicators
      : kind === "SDI"
        ? summaryData.serviceDeliveryIndicators
        : kind === "PI"
          ? summaryData.performanceIndicators
          : null;
  if (!Array.isArray(list)) return null;

  return (
    list.find((item) => {
      const no = String(item?.no || "").trim();
      if (!no || no.toLowerCase().includes("sub-total")) return false;
      return no === code || no.endsWith(code) || no.includes(` ${code}`) || no.includes(`-${code}`);
    }) ?? null
  );
}

function unwrapScoreSheetSummary(raw) {
  if (!raw || typeof raw !== "object") return null;
  if (
    raw.ciSubIndicators ||
    raw.sdiSubIndicators ||
    raw.piSubIndicators ||
    raw.complianceIndicators ||
    raw.serviceDeliveryIndicators ||
    raw.performanceIndicators
  ) {
    return raw;
  }
  if (raw.data && typeof raw.data === "object") {
    return unwrapScoreSheetSummary(raw.data);
  }
  return raw;
}

function detailRowMatchesSlug(item, slug, code) {
  if (!item) return false;
  const itemSlug = String(item.slug || "").trim().toLowerCase();
  const itemNo = String(item.no || "").trim();
  const wantedSlug = String(slug || "").trim().toLowerCase();
  return itemSlug === wantedSlug || itemNo === code || itemNo.endsWith(code);
}

export function isDpatCompletedOrClosedStatus(status) {
  const raw = String(status || "").trim().toLowerCase();
  return raw === "completed" || raw === "closed";
}

const detailListsCache = new WeakMap();

function getCachedDetailLists(summary, assessmentStartTables) {
  if (!summary || !assessmentStartTables) return null;
  let byTables = detailListsCache.get(summary);
  if (!byTables) {
    byTables = new WeakMap();
    detailListsCache.set(summary, byTables);
  }
  let lists = byTables.get(assessmentStartTables);
  if (!lists) {
    lists = buildSubIndicatorDetailLists(summary, [], assessmentStartTables);
    byTables.set(assessmentStartTables, lists);
  }
  return lists;
}

export function findDetailScoreRowForSlug(summaryData, slug, assessmentStartTables = null) {
  if (!slug) return null;
  const summary = unwrapScoreSheetSummary(summaryData);
  const kind = indicatorKindFromSlug(slug);
  const code = compactCodeFromSlug(slug);
  if (!kind || !code) return findSummaryRowForSlug(summary, slug);

  const matchRow = (item) => detailRowMatchesSlug(item, slug, code);
  const savedList =
    kind === "CI"
      ? summary?.ciSubIndicators
      : kind === "SDI"
        ? summary?.sdiSubIndicators
        : summary?.piSubIndicators;
  const saved = (savedList || []).find(matchRow) || null;

  let live = null;
  if (assessmentStartTables && typeof assessmentStartTables === "object" && Object.keys(assessmentStartTables).length) {
    const lists = getCachedDetailLists(summary, assessmentStartTables);
    live = (lists?.[kind] || []).find(matchRow) || null;
  }

  if (saved && live) {
    return {
      ...saved,
      ...live,
      technicalTeamScore: saved.technicalTeamScore || live.technicalTeamScore,
      qualityAssuranceScore: saved.qualityAssuranceScore || live.qualityAssuranceScore,
      petitionCommitteeScore: saved.petitionCommitteeScore || live.petitionCommitteeScore,
    };
  }

  return live || saved || findSummaryRowForSlug(summary, slug);
}

export function resolveSystemScoreDisplay({
  indicatorKind,
  summaryRow,
  manualScoreConfig,
  scoreSheetSummary = null,
  assessmentStartTables = null,
  slug = null,
}) {
  if (manualScoreConfig?.systemScore != null && manualScoreConfig.systemScore !== "") {
    return String(manualScoreConfig.systemScore);
  }
  if (manualScoreConfig?.fulfillment) {
    return String(manualScoreConfig.fulfillment);
  }

  const kind = indicatorKind || indicatorKindFromSlug(slug);
  const detailRow =
    slug && (scoreSheetSummary || assessmentStartTables)
      ? findDetailScoreRowForSlug(scoreSheetSummary, slug, assessmentStartTables)
      : null;
  const row = detailRow || summaryRow;

  if (kind === "CI") {
    if (row) {
      return getSystemCiFulfilled(row) ? "Fulfilled" : "Not Fulfilled";
    }
    const tableKey = slug ? DPAT_SLUG_TO_TABLE_KEY[String(slug).toLowerCase()] : null;
    if (tableKey && assessmentStartTables?.[tableKey]) {
      return extractLiveCiFulfillmentStatus(tableKey, assessmentStartTables[tableKey]);
    }
    return "—";
  }

  if (row) {
    const systemScore = getSystemNumericScore(row);
    if (systemScore != null && systemScore !== "") {
      return String(systemScore);
    }
  }

  return "—";
}

export function resolveMaxScore({
  indicatorKind,
  summaryRow,
  manualScoreConfig,
  assessmentStartTables,
  slug,
  data,
  comments,
  registeredMaxScore,
}) {
  if (indicatorKind === "CI") return null;

  const fromConfig = parseNumericScore(manualScoreConfig?.maxScore);
  if (fromConfig != null) return fromConfig;

  const fromRegistered = parseNumericScore(registeredMaxScore);
  if (fromRegistered != null) return fromRegistered;

  const fromSummary = parseNumericScore(summaryRow?.maxScore);
  if (fromSummary != null) return fromSummary;

  const fromAssessment = getMaxScoreFromAssessmentTables(assessmentStartTables, slug);
  if (fromAssessment != null) return fromAssessment;

  const fromData = parseNumericScore(data?.maxScore);
  if (fromData != null) return fromData;

  const anchor = findScoreAnchorComment(comments || []);
  const fromAnchorTables = getMaxScoreFromAssessmentTables(anchor?.dddpData?.tables, slug);
  if (fromAnchorTables != null) return fromAnchorTables;

  return null;
}

export function clampNumericManualScore(value, maxScore) {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return { value: "", exceedsMax: false };

  const numeric = Number(trimmed);
  if (!Number.isFinite(numeric) || numeric < 0) {
    return { value: trimmed, exceedsMax: false, invalid: true };
  }

  const whole = Math.round(numeric);

  if (maxScore != null && Number.isFinite(maxScore) && whole > maxScore) {
    return { value: String(Math.floor(maxScore)), exceedsMax: true };
  }

  return { value: String(whole), exceedsMax: false };
}

export function normalizeCommentDddpData(raw) {
  if (raw == null) return {};
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
      return {};
    }
  }
  if (typeof raw === "object") return raw;
  return {};
}

export function parseManualScoresFromComments(comments) {
  const result = {
    technicalTeamScore: "",
    qualityAssuranceScore: "",
    petitionCommitteeScore: "",
  };

  (comments || []).forEach((comment) => {
    const dddp = normalizeCommentDddpData(comment?.dddpData);
    Object.keys(result).forEach((key) => {
      const value = dddp[key];
      if (value != null && String(value).trim() !== "") {
        result[key] = String(value).trim();
      }
    });
  });

  return result;
}

function commentHasManualScoreKeys(comment) {
  const dddp = normalizeCommentDddpData(comment?.dddpData);
  return (
    Object.prototype.hasOwnProperty.call(dddp, MANUAL_SCORE_FIELDS.TECHNICAL_TEAM) ||
    Object.prototype.hasOwnProperty.call(dddp, MANUAL_SCORE_FIELDS.QUALITY_ASSURANCE) ||
    Object.prototype.hasOwnProperty.call(dddp, MANUAL_SCORE_FIELDS.PETITION_COMMITTEE)
  );
}

function commentHasManualScoreValues(comment) {
  const dddp = normalizeCommentDddpData(comment?.dddpData);
  return Boolean(
    String(dddp.technicalTeamScore || "").trim() ||
      String(dddp.qualityAssuranceScore || "").trim() ||
      String(dddp.petitionCommitteeScore || "").trim(),
  );
}

/**
 * Prefer a comment that already stores manual scores.
 * Fall back to any existing comment so QA/Petition can PUT scores even when
 * Technical left their override blank (POST-as-QA is unreliable on some APIs).
 */
export function findScoreAnchorComment(comments) {
  if (!Array.isArray(comments) || comments.length === 0) return null;

  const withScoreValues = comments.find(commentHasManualScoreValues);
  if (withScoreValues) return withScoreValues;

  const withScoreKeys = comments.find(commentHasManualScoreKeys);
  if (withScoreKeys) return withScoreKeys;

  const withGaps = comments.find((comment) => {
    const dddp = normalizeCommentDddpData(comment?.dddpData);
    return (
      String(comment?.gaps || "").trim() ||
      String(dddp.gapIssues || "").trim() ||
      String(dddp.gapRecommendation || "").trim()
    );
  });
  if (withGaps) return withGaps;

  const withText = comments.find((comment) => {
    const text = (comment?.comments != null && comment.comments !== "" ? comment.comments : comment?.comment) ?? "";
    return String(text).trim();
  });
  if (withText) return withText;

  return comments[0];
}

export function getManualScoreFieldForRole(roleNames) {
  const names = roleNames || [];
  if (names.includes("Superuser")) return "ALL";
  if (names.includes("DPAT TECHNICAL TEAM")) return MANUAL_SCORE_FIELDS.TECHNICAL_TEAM;
  if (names.includes("DPAT QUALITY ASSURANCE")) return MANUAL_SCORE_FIELDS.QUALITY_ASSURANCE;
  if (names.includes("DPAT PETITION COMMITTEE")) return MANUAL_SCORE_FIELDS.PETITION_COMMITTEE;
  return null;
}

export function getManualScoreUserRole(roleNames) {
  const names = roleNames || [];
  if (names.includes("DPAT TECHNICAL TEAM")) return "DPAT_TECHNICAL_TEAM";
  if (names.includes("DPAT QUALITY ASSURANCE")) return "DPAT_QUALITY_ASSURANCE";
  if (names.includes("DPAT PETITION COMMITTEE")) return "DPAT_PETITION_COMMITTEE";
  if (names.includes("Superuser")) return "DPAT_TECHNICAL_TEAM";
  return "DPAT_TECHNICAL_TEAM";
}

export function canEnterManualScores(roleNames) {
  return Boolean(getManualScoreFieldForRole(roleNames));
}

export function validateManualScoreValue({ indicatorKind, value, maxScore }) {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) {
    return { valid: true, normalized: "" };
  }

  if (indicatorKind === "CI") {
    const allowed = CI_SCORE_OPTIONS.map((option) => option.value);
    if (!allowed.includes(trimmed)) {
      return { valid: false, message: "Select Fulfilled or Not Fulfilled" };
    }
    return { valid: true, normalized: trimmed };
  }

  const numeric = Number(trimmed);
  if (!Number.isFinite(numeric) || numeric < 0) {
    return { valid: false, message: "Enter a valid score between 0 and the maximum score" };
  }
  if (!Number.isInteger(numeric)) {
    return { valid: false, message: "Score must be a whole number" };
  }
  if (maxScore == null || !Number.isFinite(maxScore)) {
    return {
      valid: false,
      message: "Maximum score is not available for this indicator yet. Refresh the page and try again.",
    };
  }
  if (numeric > maxScore) {
    return { valid: false, message: `Score cannot exceed the maximum score (${maxScore})` };
  }
  return { valid: true, normalized: String(numeric) };
}

export function mergeManualScoreDddpData(
  existingComment,
  tableCommentedId,
  data,
  fieldKey,
  value,
  allComments = null,
) {
  const base = {
    ...normalizeCommentDddpData(existingComment?.dddpData),
    indicator: tableCommentedId,
  };
  // Keep any scores already stored across comment rows for this indicator.
  const mergedScores = parseManualScoresFromComments(
    Array.isArray(allComments) ? allComments : existingComment ? [existingComment] : [],
  );
  base[MANUAL_SCORE_FIELDS.TECHNICAL_TEAM] = mergedScores.technicalTeamScore || "";
  base[MANUAL_SCORE_FIELDS.QUALITY_ASSURANCE] = mergedScores.qualityAssuranceScore || "";
  base[MANUAL_SCORE_FIELDS.PETITION_COMMITTEE] = mergedScores.petitionCommitteeScore || "";
  if (data != null) base.tables = data;
  // Always persist all three keys so later roles can update even if Technical left blank.
  base[fieldKey] = value == null ? "" : value;
  return base;
}

export function formatManualScoreDisplay(value) {
  const trimmed = String(value ?? "").trim();
  return trimmed || "—";
}
